import { useState, useCallback, useContext, useEffect } from 'react';
import { AppContext } from '../window';
import { NotificationContext } from '../context/NotificationContext';
import { updateUserCredentials } from '../api/userApi';

interface UsePushNotificationsReturn {
  isSupported: boolean;
  permission: NotificationPermission | null;
  isRequesting: boolean;
  enablePushNotifications: () => Promise<boolean>;
  disablePushNotifications: () => Promise<boolean>;
  checkAndRequestPermission: () => Promise<boolean>;
}

export const usePushNotifications = (): UsePushNotificationsReturn => {
  const { user, setUser } = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);
  const [isRequesting, setIsRequesting] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  
  // Check if notifications are supported
  const isSupported = 'Notification' in window;
  
  // Update permission state
  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission);
    }
  }, [isSupported]);
  
  // Check if user has pushStatus enabled and request permission if needed
  useEffect(() => {
    if (user?.credentials?.pushStatus === 1 && isSupported && Notification.permission === 'default') {
      // User has push enabled in backend but hasn't granted browser permission yet
      checkAndRequestPermission();
    }
  }, [user?.credentials?.pushStatus]);
  
  const checkAndRequestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      addNotification({
        message: 'Push notifications are not supported in this browser.',
        level: 'warning'
      });
      return false;
    }
    
    // If already granted, return true
    if (Notification.permission === 'granted') {
      setPermission('granted');
      return true;
    }
    
    // If denied, inform user
    if (Notification.permission === 'denied') {
      addNotification({
        message: 'Push notifications were previously blocked. Please enable them in your browser settings.',
        level: 'warning'
      });
      setPermission('denied');
      return false;
    }
    
    // Request permission
    setIsRequesting(true);
    try {
      const newPermission = await Notification.requestPermission();
      setPermission(newPermission);
      
      if (newPermission === 'granted') {
        return true;
      } else if (newPermission === 'denied') {
        addNotification({
          message: 'Push notifications were blocked. You can enable them later in your browser settings.',
          level: 'warning'
        });
        return false;
      } else {
        addNotification({
          message: 'Push notification permission was not granted.',
          level: 'info'
        });
        return false;
      }
    } catch (error) {
      console.error('Error requesting push notification permission:', error);
      addNotification({
        message: 'Failed to request push notification permission.',
        level: 'danger'
      });
      return false;
    } finally {
      setIsRequesting(false);
    }
  }, [isSupported, addNotification]);
  
  const enablePushNotifications = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    
    setIsRequesting(true);
    try {
      // First request browser permission
      const permissionGranted = await checkAndRequestPermission();
      
      if (permissionGranted) {
        // If permission granted, update backend
        const success = await updateUserCredentials({ pushStatus: 1 });
        
        if (success) {
          setUser({
            ...user,
            credentials: {
              ...user.credentials,
              pushStatus: 1
            }
          });
          
          addNotification({
            message: 'Push notifications enabled successfully!',
            level: 'success'
          });
          
          return true;
        } else {
          addNotification({
            message: 'Failed to save notification preferences. Please try again.',
            level: 'danger'
          });
          return false;
        }
      } else {
        // If permission not granted, update backend to reflect this
        await updateUserCredentials({ pushStatus: 0 });
        setUser({
          ...user,
          credentials: {
            ...user.credentials,
            pushStatus: 0
          }
        });
        return false;
      }
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      addNotification({
        message: 'Failed to enable push notifications.',
        level: 'danger'
      });
      return false;
    } finally {
      setIsRequesting(false);
    }
  }, [user, setUser, addNotification, checkAndRequestPermission]);
  
  const disablePushNotifications = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    
    setIsRequesting(true);
    try {
      // Update backend to disable push notifications
      const success = await updateUserCredentials({ pushStatus: 0 });
      
      if (success) {
        setUser({
          ...user,
          credentials: {
            ...user.credentials,
            pushStatus: 0
          }
        });
        
        addNotification({
          message: 'Push notifications disabled.',
          level: 'info'
        });
        
        return true;
      } else {
        addNotification({
          message: 'Failed to update notification preferences. Please try again.',
          level: 'danger'
        });
        return false;
      }
    } catch (error) {
      console.error('Error disabling push notifications:', error);
      addNotification({
        message: 'Failed to disable push notifications.',
        level: 'danger'
      });
      return false;
    } finally {
      setIsRequesting(false);
    }
  }, [user, setUser, addNotification]);
  
  return {
    isSupported,
    permission,
    isRequesting,
    enablePushNotifications,
    disablePushNotifications,
    checkAndRequestPermission,
  };
};